import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import settingsService from '../../services/settingsService';
import authService from '../../services/authService';

const Settings = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated } = useAuth();

  const [emailCopySuccess, setEmailCopySuccess] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);

  const emailConfirmed = user?.emailConfirmed ?? userProfile?.emailConfirmed ?? true;

  useEffect(() => {
    document.title = 'الإعدادات - دوائر';
  }, []);

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setPasswordError('يرجى إدخال كلمة المرور الحالية والجديدة');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('كلمة المرور الجديدة وتأكيدها غير متطابقتين');
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordMessage(null);
    try {
      const { data } = await settingsService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      setPasswordMessage(data?.message || 'تم تغيير كلمة المرور بنجاح');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'فشل تغيير كلمة المرور. يرجى المحاولة لاحقاً.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCopyEmail = async () => {
    const email = user?.email ?? userProfile?.email;
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setEmailCopySuccess(true);
      setTimeout(() => setEmailCopySuccess(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = email;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setEmailCopySuccess(true);
      setTimeout(() => setEmailCopySuccess(false), 2000);
    }
  };

  const handleResendVerification = async (e) => {
    e?.preventDefault();
    const email = user?.email ?? userProfile?.email;
    if (!email) {
      setResendError('لا يوجد بريد إلكتروني');
      return;
    }
    setResendLoading(true);
    setResendError(null);
    setResendMessage(null);
    try {
      const { data } = await authService.resendVerification(email);
      setResendMessage(data?.message || 'تم إرسال رابط التحقق');
    } catch (err) {
      setResendError(err.response?.data?.message || 'فشل إرسال الرابط');
    } finally {
      setResendLoading(false);
    }
  };

  if (!isAuthenticated) {
    navigate('/signup');
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />

      <main className="pt-16 lg:pt-20">
        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] mb-8 transition-colors"
              style={{ fontFamily: 'Amiri, serif' }}
            >
              <Icon name="ArrowRight" size={20} />
              <span>عودة</span>
            </button>

            <h1
              className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-2"
              style={{ fontFamily: 'Aref Ruqaa, serif' }}
            >
              الإعدادات
            </h1>
            <p
              className="text-[var(--color-muted-foreground)] mb-10"
              style={{ fontFamily: 'Amiri, serif' }}
            >
              إدارة إعدادات حسابك وكلمة المرور
            </p>

            {/* Account / Email Section - like Google, GitHub account info */}
            <div className="bg-[var(--color-card)] rounded-2xl p-6 md:p-8 shadow-warm mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center">
                  <Icon name="Mail" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h2
                    className="text-xl font-bold text-[var(--color-foreground)]"
                    style={{ fontFamily: 'Aref Ruqaa, serif' }}
                  >
                    معلومات الحساب
                  </h2>
                  <p
                    className="text-sm text-[var(--color-muted-foreground)]"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    البريد الإلكتروني المرتبط بحسابك
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-[var(--color-background)]/50 border border-[var(--color-border)]">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[var(--color-muted-foreground)] mb-1" style={{ fontFamily: 'Amiri, serif' }}>
                    البريد الإلكتروني المسجل
                  </p>
                  <p
                    dir="ltr"
                    className="text-base font-medium text-[var(--color-foreground)] truncate"
                    style={{ fontFamily: 'system-ui, sans-serif' }}
                  >
                    {user?.email ?? userProfile?.email ?? '—'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      emailConfirmed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {emailConfirmed ? 'مؤكد' : 'غير مؤكد'}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    iconName={emailCopySuccess ? 'Check' : 'Copy'}
                    onClick={handleCopyEmail}
                    className="rounded-lg"
                  >
                    {emailCopySuccess ? 'تم النسخ' : 'نسخ'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-[var(--color-card)] rounded-2xl p-6 md:p-8 shadow-warm mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center">
                  <Icon name="Lock" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h2
                    className="text-xl font-bold text-[var(--color-foreground)]"
                    style={{ fontFamily: 'Aref Ruqaa, serif' }}
                  >
                    تغيير كلمة المرور
                  </h2>
                  <p
                    className="text-sm text-[var(--color-muted-foreground)]"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    حدّث كلمة المرور لحسابك
                  </p>
                </div>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <Input
                  type="password"
                  label="كلمة المرور الحالية"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))
                  }
                  placeholder="أدخل كلمة المرور الحالية"
                  dir="rtl"
                />
                <Input
                  type="password"
                  label="كلمة المرور الجديدة"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))
                  }
                  placeholder="8 أحرف على الأقل"
                  dir="rtl"
                />
                <Input
                  type="password"
                  label="تأكيد كلمة المرور الجديدة"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))
                  }
                  placeholder="أعد إدخال كلمة المرور الجديدة"
                  dir="rtl"
                />
                {passwordMessage && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-xl text-sm text-center">
                    {passwordMessage}
                  </div>
                )}
                {passwordError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center">
                    {passwordError}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="rounded-full px-8 py-3"
                  style={{
                    fontFamily: 'Amiri, serif',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                  }}
                >
                  {passwordLoading ? 'جاري الحفظ...' : 'تغيير كلمة المرور'}
                </Button>
              </form>
            </div>

            {/* Resend Verification - only if email not confirmed */}
            {!emailConfirmed && (
              <div className="bg-[var(--color-card)] rounded-2xl p-6 md:p-8 shadow-warm mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[var(--color-warning)]/20 flex items-center justify-center">
                    <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
                  </div>
                  <div>
                    <h2
                      className="text-xl font-bold text-[var(--color-foreground)]"
                      style={{ fontFamily: 'Aref Ruqaa, serif' }}
                    >
                      تأكيد البريد الإلكتروني
                    </h2>
                    <p
                      className="text-sm text-[var(--color-muted-foreground)]"
                      style={{ fontFamily: 'Amiri, serif' }}
                    >
                      لم يتم تأكيد بريدك بعد. يرجى النقر على الرابط المرسل إليك.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleResendVerification} className="space-y-4">
                  {resendMessage && (
                    <div className="bg-green-50 text-green-700 p-3 rounded-xl text-sm text-center">
                      {resendMessage}
                    </div>
                  )}
                  {resendError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center">
                      {resendError}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={resendLoading}
                    variant="outline"
                    className="rounded-full px-8 py-3"
                    style={{
                      fontFamily: 'Amiri, serif',
                      borderColor: 'var(--color-primary)',
                      color: 'var(--color-primary)',
                    }}
                  >
                    {resendLoading ? 'جاري الإرسال...' : 'إعادة إرسال رابط التحقق'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Settings;
